import { ChromecastSessionManager } from './chromecastSessionManager';
import { createMediaInfo } from './mediaInfoFactory';

export class ChromecastController {
  private readonly session: ChromecastSessionManager;

  private readonly remotePlayer: cast.framework.RemotePlayer;

  private readonly remotePlayerController: cast.framework.RemotePlayerController;

  constructor(session: ChromecastSessionManager) {
    this.session = session;
    this.remotePlayer = new cast.framework.RemotePlayer();
    this.remotePlayerController = new cast.framework.RemotePlayerController(
      this.remotePlayer
    );
  }

  public addEventListener(
    type: cast.framework.RemotePlayerEventType,
    handler: (event: cast.framework.RemotePlayerChangedEvent) => void,
    options?: { once?: boolean }
  ) {
    if (options?.once) {
      const wrappedHandler = (
        event: cast.framework.RemotePlayerChangedEvent
      ) => {
        handler(event);

        this.remotePlayerController.removeEventListener(type, wrappedHandler);
      };

      this.remotePlayerController.addEventListener(type, wrappedHandler);
    } else {
      this.remotePlayerController.addEventListener(type, handler);
    }
  }

  public removeEventListener(
    type: cast.framework.RemotePlayerEventType,
    handler: (event: cast.framework.RemotePlayerChangedEvent) => void
  ) {
    this.remotePlayerController.removeEventListener(type, handler);
  }

  private withInvalidStateCheck(callback: () => void) {
    try {
      callback();
    } catch (err) {
      console.error(err);

      // when a mobile device goes idle, the cast connection can be terminated
      // without explicitly closing it - when this happens, the cast session is
      // unusable and throws exceptions, so that's why we need to disconnect explicitly
      if (err instanceof DOMException && err.name === 'InvalidStateError') {
        this.disconnect();

        return;
      }

      throw err;
    }
  }

  public play() {
    if (this.remotePlayer.isPaused) {
      this.withInvalidStateCheck(() => {
        this.remotePlayerController.playOrPause();
      });
    }
  }

  public pause() {
    if (!this.remotePlayer.isPaused) {
      this.withInvalidStateCheck(() => {
        this.remotePlayerController.playOrPause();
      });
    }
  }

  public mute(state?: boolean): void {
    if (this.remotePlayer.isMuted !== Boolean(state)) {
      this.withInvalidStateCheck(() => {
        this.remotePlayerController.muteOrUnmute();
      });
    }
  }

  public setVolume(volume: number): void {
    this.withInvalidStateCheck(() => {
      this.session.getSession()?.setVolume(volume);
    });
  }

  public getVolume(): number {
    return (
      this.session.getSession()?.getVolume() || this.remotePlayer.volumeLevel
    );
  }

  public seek(seekPos: number): void {
    this.remotePlayer.currentTime = seekPos;

    this.withInvalidStateCheck(() => {
      this.remotePlayerController.seek();
    });

    this.play();
  }

  public getEmbeddedCaptions() {
    const media = this.session.getMedia()?.media;

    return (media?.tracks || []).filter(
      (e) => e.type === 'TEXT' && e.customData !== 'side-loaded captions'
    );
  }

  public async enableCaptions(captionsId: string): Promise<boolean> {
    const media = this.session.getMedia()?.media;

    const tracks = (media?.tracks || []).filter(
      (track) =>
        track.type !== chrome.cast.media.TrackType.TEXT ||
        track.name === captionsId
    );

    if (tracks.length < 1) {
      return false;
    }

    const request = new chrome.cast.media.EditTracksInfoRequest(
      tracks.map((track) => track.trackId)
    );

    return this.session.editTracksInfo(request);
  }

  public async disableCaptions(): Promise<boolean> {
    const media = this.session.getMedia()?.media;

    const tracks = (media?.tracks || []).filter(
      (track) => track.type !== chrome.cast.media.TrackType.TEXT
    );

    const request = new chrome.cast.media.EditTracksInfoRequest(
      tracks.map((track) => track.trackId)
    );

    return this.session.editTracksInfo(request);
  }

  public disconnect(): void {
    this.session.disconnect();
  }

  public isConnected() {
    return this.remotePlayer.isConnected;
  }

  public getDeviceName() {
    return this.session.getSession()?.getCastDevice().friendlyName ?? '';
  }

  public isAvailable() {
    return (
      this.session.getCastContext().getCastState() !==
      cast.framework.CastState.NO_DEVICES_AVAILABLE
    );
  }

  public isMuted() {
    return this.remotePlayer.isMuted;
  }

  public getState() {
    return this.remotePlayer.playerState;
  }

  public isComplete() {
    const sessionState = this.session.getSessionState();
    const state = this.getState();
    const duration = this.getDuration();
    const currentTime = this.getCurrentTime();
    // `.idleReason` seems to be populated correctly only in media returned from `getSessionObj()` method,
    // so we need to use that instead of already present `getMedia()`
    const media = this.session.getSession()?.getSessionObj().media[0];

    // sometimes chromecast might get stuck in the buffering state
    // instead of transitioning to the idle state
    if (
      sessionState &&
      state === chrome.cast.media.PlayerState.BUFFERING &&
      duration &&
      currentTime &&
      Math.abs(duration - currentTime) < 0.1
    ) {
      return true;
    }

    return (
      sessionState &&
      state === chrome.cast.media.PlayerState.IDLE &&
      media?.idleReason === chrome.cast.media.IdleReason.FINISHED
    );
  }

  public isPlayingBreak() {
    return !!this.getCurrentBreakClip();
  }

  public getLiveSeekableRange() {
    if (this.isPlayingBreak()) {
      return undefined;
    }

    return this.session.getMedia()?.liveSeekableRange;
  }

  public getDuration() {
    const breakClip = this.getCurrentBreakClip();

    if (breakClip) {
      return breakClip.duration ?? 0;
    }

    return this.session.getMedia()?.media?.duration ?? 0;
  }

  public getCurrentBreakClip() {
    const breakClipId = this.session.getMedia()?.breakStatus?.breakClipId;
    const breakClips = this.session.getMedia()?.media?.breakClips;

    if (!breakClipId || !breakClips) {
      return undefined;
    }

    const breakClipIndex = breakClips.findIndex(
      (breakClip) => breakClip.id === breakClipId
    );

    if (breakClipIndex === -1) {
      return undefined;
    }

    return {
      ...breakClips[breakClipIndex],
      sequence: breakClipIndex + 1,
      podcount: breakClips.length,
    };
  }

  public getCurrentTime(): number {
    const media = this.session.getMedia();

    if (!media) {
      return 0;
    }

    if (this.isPlayingBreak()) {
      return media.getEstimatedBreakClipTime();
    }

    return media.getEstimatedTime();
  }

  public async load({
    playlistItem,
    startTime,
    vmapUrl,
  }: {
    playlistItem: jwplayer.PlaylistItem;
    startTime?: number;
    vmapUrl?: string;
  }): Promise<void> {
    await this.loadPlaylistItem(playlistItem, vmapUrl, startTime);
  }

  private async loadPlaylistItem(
    playlistItem: jwplayer.PlaylistItem,
    vmapUrl?: string,
    startTime?: number
  ): Promise<void> {
    const mediaInfo = await createMediaInfo(playlistItem, vmapUrl);

    if (!mediaInfo) {
      return;
    }

    const request = new chrome.cast.media.LoadRequest(mediaInfo);
    request.currentTime = startTime ?? 0;

    await this.session.loadMedia(request);
  }
}
